import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { changeAccountLanguage, changeLanguage } from '@/store/slices/application';
import { saveUserPreferences, PREFERENCE_KEYS } from '@/store/slices/userPreferences';
import { markManualLanguagePick } from '@/nova/config/manualLanguagePick';
import { toBackendLang } from '@/nova/config/langCodeMap';
import { useFlutterEvents } from '@/components/utils/hooks/events/useFlutterEvents';
import { GLOBAL_EVENTS, GLOBAL_FLUTTER_CHANNELS } from '@/components/utils/hooks/events/constants';

// The endonym map + `languageName` live in a store-free config module so presentational
// pickers (and their component tests) can use them without pulling in this Redux-coupled hook.
// Re-exported here for the existing call sites.
export { LANGUAGE_NAMES, languageName } from '@/nova/config/languageEndonyms';

export interface LanguageSwitcher {
  /** The languages offered: `window.config.languages` (empty if unset), or, when the
   *  caller passes a non-empty `allowed` scope, that same list narrowed to `allowed`
   *  (keeping `window.config` order). */
  languages: string[];
  /** Active language as the short code the option ids use ('en', not 'en-US'). */
  currentLanguage: string;
  /** Switch language: updates the store, i18next, the native shell, and (when
   *  authenticated) the account's server-side preferences. */
  onChangeLanguage: (language: string) => void;
}

/**
 * Shared language-switcher wiring used by the full-bleed auth screens (`/login`,
 * `/register` `LanguagePicker`), the configurator account popover, and the
 * Settings -> Interface "Preferred language" dropdown. Reproduces the config-driven
 * language-switcher module: it dispatches `changeLanguage` + `changeAccountLanguage`,
 * calls `i18n.changeLanguage`, and emits the native `CHANGE_LANGUAGE` event so the
 * Flutter/Cordova shell re-localises its chrome too. When authenticated it ALSO
 * saves the choice to the account's server-side preferences (`PREFERENCE_KEYS.language`)
 * so it follows the user across devices. The active code is normalised down to the
 * short form ('en-US' -> 'en') so the picker's active highlight + name match on first load.
 *
 * The language-change contract lives in this one place (originally extracted from the
 * byte-identical copies in `pages/login` and `pages/register`).
 */
export const useLanguageSwitcher = (allowed?: readonly string[]): LanguageSwitcher => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const events = useFlutterEvents();

  const rawLanguage: string = useAppSelector((state) => state.application.language) || i18n.language || '';
  const currentLanguage: string = rawLanguage.split('-')[0];
  const configLanguages: string[] = window.config.languages ?? [];
  // Optional scope: when the caller passes a non-empty `allowed` set (e.g. a region's
  // languages), offer only the configured codes that are in it, keeping `window.config`
  // order. Backwards-compatible: with no scope the full configured set is offered.
  const languages: string[] =
    allowed && allowed.length > 0 ? configLanguages.filter((code) => allowed.includes(code)) : configLanguages;
  // Persist to the account's server-side preferences only when authenticated — the
  // `/login` + `/register` pickers have no token yet, so a POST there would just 401.
  const authenticated = useAppSelector((state) => Boolean(state.authentication.access_token));

  const onChangeLanguage = React.useCallback(
    (language: string) => {
      // `changeLanguage` reducer already calls `i18n.changeLanguage` internally
      // (store/slices/application.ts), so we don't repeat it here.
      dispatch(changeLanguage(language));
      dispatch(changeAccountLanguage(language));
      // Tell the native shell (Flutter/Cordova) so its chrome re-localises too.
      events.send(GLOBAL_FLUTTER_CHANNELS.POKER_APP_EVENT, {
        event: GLOBAL_EVENTS.CHANGE_LANGUAGE,
        payload: { language },
      });
      // Record the EXPLICIT UI choice for this session (a dedicated key, NOT the startProperties
      // region cue). Unconditional, because an unauthenticated pick on `/login` / `/register` has
      // no token to persist yet, so this is what carries it across the auth transition: the
      // configurator flushes it to user-preferences once authenticated, and OwsTheme's
      // saved-language adoption won't revert it to the fresh account's default pref.
      markManualLanguagePick(language);
      // Save the choice to user preferences so it follows the account across devices
      // (on top of the `language` localStorage the reducer already writes per-browser).
      if (authenticated) dispatch(saveUserPreferences({ [PREFERENCE_KEYS.language]: toBackendLang(language) }));
    },
    [dispatch, events, authenticated],
  );

  return { languages, currentLanguage, onChangeLanguage };
};

export default useLanguageSwitcher;
